import isNil from "../validation/isNil";

type ValueTuple<Results extends readonly Result<unknown, unknown>[]> = {
  [i in keyof Results]: Results[i] extends Result<infer T, unknown> ? T : never;
} & { length: Results["length"] };

type ErrorUnion<Results extends readonly Result<unknown, unknown>[]> =
  Results extends readonly Result<unknown, infer E>[] ? E : never;

type AllResult<Results extends readonly Result<unknown, unknown>[]> = Result<
  ValueTuple<Results>,
  ErrorUnion<Results>
>;

const Value = Symbol("Value");

export type Result<T, E> = Ok<T> | Err<E>;

export const Result = {
  ok: <T, E>(value: T): Result<T, E> => {
    return new Ok<T>(value);
  },

  error: <T, E>(error: E): Result<T, E> => {
    return new Err(error);
  },

  all: <Results extends readonly Result<unknown, unknown>[]>(
    ...results: Results
  ): AllResult<Results> => {
    const firstError = results.find((r) => r.isSuccessful === false);

    if (isNil(firstError)) {
      const values = results.map((r) => r.unwrapOr(null as never));
      return Result.ok(values) as AllResult<Results>;
    }

    return firstError as AllResult<Results>;
  },
  safe: async <T, E>(promise: Promise<T>): Promise<Result<T, E>> => {
    return promise
      .then((val) => new Ok<T>(val))
      .catch((error) => new Err<E>(error));
  },
};

class Ok<T> {
  readonly isSuccessful: true;
  readonly isErr = false;
  readonly [Value]: T;

  constructor(value: T) {
    this[Value] = value;
    this.isSuccessful = true;
  }

  unsafeUnwrap(): T {
    return this[Value];
  }

  unwrapOr(): T {
    return this[Value];
  }

  unwrapErrOr<F>(fallback: F): never | F {
    return fallback;
  }

  unwrapErr(): never {
    throw new Error(`Expected error but got ${this[Value]}`);
  }

  mapError() {
    return this;
  }

  map<Out>(callback: (value: T) => Out): Result<Out, never> {
    const value = this[Value];
    return Result.ok(callback(value));
  }

  into(): T {
    return this[Value];
  }

  andThen<OutSuccess, OutError>(
    callback: (value: T) => Result<OutSuccess, OutError>,
  ): Result<OutSuccess, OutError> {
    const value = this[Value];
    return callback(value);
  }
}

class Err<E> {
  readonly isSuccessful = false as const;
  readonly isErr = true as const;
  readonly [Value]: E;

  constructor(value: E) {
    this[Value] = value;
  }

  unsafeUnwrap(): never {
    throw new Error(`Expected result to be ok but got error: ${this[Value]}`);
  }

  unwrapOr<Fallback>(fallback: Fallback): Fallback {
    return fallback;
  }

  unwrapErrOr(): E {
    return this[Value];
  }

  unwrapErr(): E {
    return this[Value];
  }

  mapError<OutError>(callback: (e: E) => OutError): Result<never, OutError> {
    const error = this[Value];
    return Result.error(callback(error));
  }

  map(): Result<never, E> {
    return this;
  }

  into(): E {
    return this[Value];
  }

  andThen(): Result<never, E> {
    return this;
  }
}
